<template>
  <div v-if="backgroundColor || backgroundType" class="v-icon-wrapper" :class="{ rounded, [`v-icon-wrapper--${backgroundType}`]: backgroundType }" :style="wrapperStyle">
    <Component :is="component" v-if="component" v-bind="attrs" />
    <svg v-else fill="none" :height="size" :width="size" xmlns="http://www.w3.org/2000/svg" v-bind="attrs">
      <slot />
    </svg>
  </div>
  <Component :is="component" v-else-if="component" v-bind="attrs" />
  <svg v-else fill="none" :height="size" :width="size" xmlns="http://www.w3.org/2000/svg" v-bind="attrs">
    <slot />
  </svg>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { V_ICON_COMPONENTS, type VIconIcon, type VIconType } from '../../types.ts'
import { levenshtein } from '../../utils/levenshtein.ts'

interface Props {
  backgroundColor?: string
  backgroundType?: VIconType
  icon?: VIconIcon | ''
  mirrored?: boolean
  rotate?: boolean
  rounded?: boolean
  size?: number
  strokeWidth?: number
  type?: VIconType
  viewBox?: string
}

const props = withDefaults(defineProps<Props>(), {
  backgroundColor: undefined,
  backgroundType: undefined,
  icon: '',
  mirrored: false,
  rotate: false,
  rounded: true,
  size: 15,
  strokeWidth: 0,
  type: 'black',
  viewBox: undefined,
})

defineSlots<{
  default?(): any
}>()

if (props.icon && !(props.icon in V_ICON_COMPONENTS)) {
  let error = `Invalid icon: "${props.icon}"`
  for (const icon in V_ICON_COMPONENTS) {
    if (levenshtein(props.icon, icon) < 4) {
      error += `\nDid you mean "${icon}"?`
    }
  }
  throw new Error(error)
}

const attrs = computed(() => {
  const icon = `v-icon-${props.icon.toLowerCase()}`
  const type = `v-icon--${props.type}`

  const attrs = {
    'aria-labelledby': 'icon',
    class: [
      'v-icon',
      icon,
      type,
      {
        'v-icon--rotate': props.rotate,
        'v-icon--mirrored': props.mirrored,
      },
    ],
    style: { width: 'auto', height: `${props.size}px` },
    role: 'presentation',
    viewBox: undefined as undefined | string,
    'stroke-width': undefined as undefined | number,
  }

  if (props.viewBox) {
    attrs.viewBox = props.viewBox
  } else {
    delete attrs.viewBox
  }

  if (props.strokeWidth) {
    attrs['stroke-width'] = props.strokeWidth
  } else {
    delete attrs['stroke-width']
  }

  return attrs
})

const wrapperStyle = computed(() => ({
  backgroundColor: props.backgroundColor,
  width: `${props.size * 2}px`,
  height: `${props.size * 2}px`,
}))

const component = computed(() => (props.icon ? V_ICON_COMPONENTS[props.icon] : undefined))
</script>

<style>
.v-icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.v-icon-wrapper.rounded {
  border-radius: 50%;
}

.v-icon {
  transition:
    color,
    opacity,
    transform var(--transition);
  flex-shrink: 0;
}

.v-icon--rotate {
  animation: var(--rotate-animation);
}

.v-icon--mirrored {
  transform: rotateY(180deg);
}

.v-icon--black * {
  stroke: #000;
}

.v-icon--black *.v-icon-fill-color,
.v-icon--black * .v-icon-fill-color {
  fill: #000;
}

.v-icon-wrapper--black {
  background-color: #000;
}

.v-icon--white * {
  stroke: #fff;
}

.v-icon--white *.v-icon-fill-color,
.v-icon--white * .v-icon-fill-color {
  fill: #fff;
}

.v-icon-wrapper--white {
  background-color: #fff;
}

.v-icon--primary * {
  stroke: var(--color-primary);
}

.v-icon--primary *.v-icon-fill-color,
.v-icon--primary * .v-icon-fill-color {
  fill: var(--color-primary);
}

.v-icon-wrapper--primary {
  background-color: var(--color-primary);
}

.v-icon--secondary * {
  stroke: var(--color-secondary);
}

.v-icon--secondary *.v-icon-fill-color,
.v-icon--secondary * .v-icon-fill-color {
  fill: var(--color-secondary);
}

.v-icon-wrapper--secondary {
  background-color: var(--color-secondary);
}

.v-icon--tertiary * {
  stroke: var(--color-tertiary);
}

.v-icon--tertiary *.v-icon-fill-color,
.v-icon--tertiary * .v-icon-fill-color {
  fill: var(--color-tertiary);
}

.v-icon-wrapper--tertiary {
  background-color: var(--color-tertiary);
}

.v-icon--green * {
  stroke: var(--color-green);
}

.v-icon--green *.v-icon-fill-color,
.v-icon--green * .v-icon-fill-color {
  fill: var(--color-green);
}

.v-icon-wrapper--green {
  background-color: var(--color-green);
}

.v-icon--orange * {
  stroke: var(--color-orange);
}

.v-icon--orange *.v-icon-fill-color,
.v-icon--orange * .v-icon-fill-color {
  fill: var(--color-orange);
}

.v-icon-wrapper--orange {
  background-color: var(--color-orange);
}

.v-icon--red * {
  stroke: var(--color-red);
}

.v-icon--red *.v-icon-fill-color,
.v-icon--red * .v-icon-fill-color {
  fill: var(--color-red);
}

.v-icon-wrapper--red {
  background-color: var(--color-red);
}

.v-icon--info * {
  stroke: var(--color-info);
}

.v-icon--info *.v-icon-fill-color,
.v-icon--info * .v-icon-fill-color {
  fill: var(--color-info);
}

.v-icon-wrapper--info {
  background-color: var(--color-info);
}

.v-icon--placeholder * {
  stroke: var(--color-text-placeholder);
}

.v-icon--placeholder *.v-icon-fill-color,
.v-icon--placeholder * .v-icon-fill-color {
  fill: var(--color-text-placeholder);
}

.v-icon-wrapper--placeholder {
  background-color: var(--color-text-placeholder);
}

.v-icon--blue-1 * {
  stroke: var(--color-blue-1);
}

.v-icon--blue-1 *.v-icon-fill-color,
.v-icon--blue-1 * .v-icon-fill-color {
  fill: var(--color-blue-1);
}

.v-icon-wrapper--blue-1 {
  background-color: var(--color-blue-1);
}

.v-icon--blue-3 * {
  stroke: var(--color-blue-3);
}

.v-icon--blue-3 *.v-icon-fill-color,
.v-icon--blue-3 * .v-icon-fill-color {
  fill: var(--color-blue-3);
}

.v-icon-wrapper--blue-3 {
  background-color: var(--color-blue-3);
}

.v-icon--blue-5 * {
  stroke: var(--color-blue-5);
}

.v-icon--blue-5 *.v-icon-fill-color,
.v-icon--blue-5 * .v-icon-fill-color {
  fill: var(--color-blue-5);
}

.v-icon-wrapper--blue-5 {
  background-color: var(--color-blue-5);
}

.v-icon-close {
  stroke-width: 0.5;
}

.v-icon-close,
.v-icon-closecircle {
  transform: rotate(45deg);
}

.v-icon-chevronright.is-expanded {
  transform: rotate(90deg);
}

.v-icon-chevrondown.is-expanded {
  transform: rotate(180deg);
}

.v-icon-grab[draggable='true'] {
  cursor: grab;
}

.is-dragging .v-icon-grab[draggable='true'] {
  cursor: grabbing;
}
</style>
